import React, { useEffect, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, ChevronDown, ListOrdered } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import DocsBreadcrumb from './DocsBreadcrumb';
import KeyboardShortcutsOverlay from './KeyboardShortcutsOverlay';
import BackToTop from './BackToTop';

const bgImage = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/69qn1rnj_ABSTRACT%20DESIGN.png';

/** Ordered list for Notification containers — used for Prev/Next pagination. */
export const NOTIFICATION_CONTAINERS = [
  { id: 'confirmation', title: 'Confirmation Notification', link: '/technical-documentation/notifications/confirmation' },
  { id: 'chargeback',   title: 'Chargeback Notification',   link: '/technical-documentation/notifications/chargeback'   },
  { id: 'fraud',        title: 'Fraud Notification',        link: '/technical-documentation/notifications/fraud'        },
];

const NotificationPagination = ({ currentId }) => {
  const idx = NOTIFICATION_CONTAINERS.findIndex((c) => c.id === currentId);
  const prev = idx > 0 ? NOTIFICATION_CONTAINERS[idx - 1] : null;
  const next = idx < NOTIFICATION_CONTAINERS.length - 1 ? NOTIFICATION_CONTAINERS[idx + 1] : null;

  const Card = ({ item, side }) => {
    if (!item) return <div className="flex-1" />;
    const isPrev = side === 'prev';
    return (
      <Link
        to={item.link}
        className={`group flex-1 flex items-center gap-3 rounded-lg border border-gray-200 bg-white/90 hover:bg-white hover:border-[#1F4E79] hover:shadow-md p-4 transition-all duration-200 ${isPrev ? '' : 'flex-row-reverse text-right'}`}
        data-testid={`notif-pagination-${side}`}
      >
        {isPrev ? <ChevronLeft size={20} className="text-[#1F4E79] flex-shrink-0" />
                : <ChevronRight size={20} className="text-[#1F4E79] flex-shrink-0" />}
        <div>
          <p className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em]">{isPrev ? 'Previous' : 'Next'}</p>
          <p className="font-bold text-[#1F4E79] group-hover:text-[#163a5f] text-sm">{item.title}</p>
        </div>
      </Link>
    );
  };

  return (
    <nav className="flex flex-col sm:flex-row gap-4 mt-8" data-testid="notif-pagination">
      <Card item={prev} side="prev" />
      <Card item={next} side="next" />
    </nav>
  );
};

/**
 * Thin wrapper for Notification container pages — mirrors ContainerPageLayout visually
 * but:
 *  - "Back to Notifications" button (not API Reference)
 *  - Pagination uses NOTIFICATION_CONTAINERS
 */
const NotificationPageLayout = ({
  currentId,
  title,
  subtitle,
  titleBadge,
  toc = [],
  mainTestId = 'notification-page',
  sidebarTestId = 'notification-sidebar',
  children,
}) => {
  const navigate = useNavigate();
  const flatIds = toc.flatMap((i) => [i.id, ...(i.children?.map((c) => c.id) || [])]);
  const [activeId, setActiveId] = useState(flatIds[0] || '');

  useEffect(() => {
    if (flatIds.length === 0) return undefined;
    const handleScroll = () => {
      const offset = 140;
      let current = flatIds[0];
      for (const id of flatIds) {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top - offset <= 0) current = id;
      }
      setActiveId(current);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNavClick = (e, id) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
    setMobileTocOpen(false);
  };

  const [mobileTocOpen, setMobileTocOpen] = useState(false);

  const flatToc = toc.flatMap((item) =>
    item.children
      ? [{ id: item.id, label: item.label, level: 2 }, ...item.children.map((c) => ({ id: c.id, label: c.label, level: 3 }))]
      : [{ id: item.id, label: item.label, level: 2 }]
  );

  const renderTocNav = (idPrefix = '') => (
    <nav className="py-2">
      {toc.map((item) => (
        <div key={item.id}>
          <a
            href={`#${item.id}`}
            onClick={(e) => handleNavClick(e, item.id)}
            className={`block px-4 py-2 text-sm transition-colors duration-150 border-l-2 ${
              activeId === item.id
                ? 'border-[#1F4E79] bg-blue-50 text-[#1F4E79] font-semibold'
                : 'border-transparent text-gray-700 hover:bg-gray-50 hover:text-[#1F4E79]'
            }`}
            data-testid={`${idPrefix}toc-link-${item.id}`}
          >
            {item.label}
          </a>
          {item.children && (
            <div className="ml-3">
              {item.children.map((child) => (
                <a
                  key={child.id}
                  href={`#${child.id}`}
                  onClick={(e) => handleNavClick(e, child.id)}
                  className={`block px-4 py-1.5 text-xs transition-colors duration-150 border-l-2 ${
                    activeId === child.id
                      ? 'border-[#1F4E79] bg-blue-50/60 text-[#1F4E79] font-semibold'
                      : 'border-transparent text-gray-600 hover:bg-gray-50 hover:text-[#1F4E79]'
                  }`}
                  data-testid={`${idPrefix}toc-link-${child.id}`}
                >
                  {child.label}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );

  return (
    <div className="min-h-screen flex flex-col scroll-smooth" data-testid={mainTestId}>
      <Header />
      <div className="h-20" />
      <div className="relative flex-1">
        <div
          className="fixed inset-0 top-20 bg-cover bg-center"
          style={{ backgroundImage: `url(${bgImage})` }}
        />

        <div className="relative z-10 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-10">
          <DocsBreadcrumb section="notifications" currentId={currentId} />
          <button
            onClick={() => navigate('/technical-documentation/notifications')}
            className="flex items-center gap-2 text-[#1F4E79] hover:text-[#163a5f] font-medium mb-8 transition-colors duration-200"
            data-testid={`${mainTestId}-back-button`}
          >
            <ArrowLeft size={18} />
            Back to Notifications
          </button>

          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_256px] lg:gap-8">
            <main className="min-w-0">
              {/* Mobile collapsed TOC pill */}
              {flatToc.length > 0 && (
                <div className="lg:hidden mb-6 border border-gray-200 rounded-xl overflow-hidden bg-white/90 backdrop-blur-sm">
                  <button
                    type="button"
                    onClick={() => setMobileTocOpen((o) => !o)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 text-left"
                    aria-expanded={mobileTocOpen}
                    data-testid="mobile-toc-toggle"
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-[#0a1929]">
                      <ListOrdered size={16} className="text-[#ef5b25]" />
                      On this page
                      <span className="text-gray-400 font-normal">({flatToc.length})</span>
                    </span>
                    <ChevronDown
                      size={18}
                      className={`text-gray-500 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {mobileTocOpen && (
                    <div className="bg-white max-h-[60vh] overflow-y-auto" data-testid="mobile-toc-panel">
                      {renderTocNav('m-')}
                    </div>
                  )}
                </div>
              )}

              {/* Title card */}
              <div className="bg-white/80 backdrop-blur-sm rounded-lg shadow-lg p-8 sm:p-12 mb-8">
                <div className="flex items-center gap-3 mb-3 flex-wrap">
                  <h1 className="text-4xl sm:text-5xl font-bold text-gray-900" data-testid={`${mainTestId}-title`}>
                    {title}
                  </h1>
                  {titleBadge && (
                    <span className="inline-flex items-center px-2 py-1 rounded text-xs font-bold bg-[#1F4E79] text-white uppercase tracking-wide">
                      {titleBadge}
                    </span>
                  )}
                </div>
                {subtitle && <p className="text-gray-600 text-lg">{subtitle}</p>}
              </div>

              {children}

              <NotificationPagination currentId={currentId} />
            </main>

            {/* Sticky desktop TOC */}
            {toc.length > 0 && (
              <aside
                className="hidden lg:block lg:sticky lg:top-28 self-start rounded-lg shadow-md border border-gray-200 bg-white/85 backdrop-blur-sm overflow-hidden"
                data-testid={sidebarTestId}
              >
                <div className="px-4 py-3 bg-[#1F4E79] text-white">
                  <p className="text-sm font-semibold tracking-wide">On This Page</p>
                </div>
                <div className="max-h-[calc(100vh-10rem)] overflow-y-auto">{renderTocNav()}</div>
              </aside>
            )}
          </div>
        </div>
      </div>

      <div className="relative z-10">
        <Footer />
      </div>
      <KeyboardShortcutsOverlay />
      <BackToTop />
    </div>
  );
};

/** Shared dark CodeBlock — re-exported so container pages can use it directly. */
export const CodeBlock = ({ code, lang = 'xml', testid }) => (
  <div className="relative bg-[#1e1e1e] rounded-lg overflow-hidden border border-gray-700 my-4" data-testid={testid}>
    <div className="flex items-center justify-between px-4 py-2 bg-[#2d2d2d] border-b border-gray-700">
      <span className="text-xs font-mono text-gray-400 uppercase tracking-wide">{lang}</span>
      <button
        onClick={() => navigator.clipboard?.writeText(code)}
        className="text-xs text-gray-400 hover:text-white transition-colors duration-150 font-medium"
        data-testid={`${testid}-copy`}
      >
        Copy
      </button>
    </div>
    <pre className="overflow-x-auto p-4 text-sm leading-relaxed">
      <code className={`language-${lang} text-gray-100 font-mono whitespace-pre`}>{code}</code>
    </pre>
  </div>
);

export default NotificationPageLayout;
